import * as React from "react";
import { useNavigate } from "react-router";

import type {
    JourneyInstanceDTO,
    JourneyInstanceMetadata,
    JourneyWithRelations,
} from "@volley/data";

import { fetchApi } from "../../../util";
import { logFetchError } from "../../../util/fetchApi";
import { useSelectedSport } from "../../common/context/sport";
import useJourney from "../../hooks/useJourney";
import AssessmentStartDialog from "../JourneyAssessment/AssessmentStartDialog";
import { inProgressUrl } from "../journeyNav";

export interface AssessmentStartState {
    /** Open the start dialog by journey ID (fetches journey data first) */
    openStart: (journeyId: number) => void;
    /** Open the start dialog with a pre-fetched journey (no extra fetch) */
    openStartWithJourney: (journey: JourneyWithRelations) => void;
    /** The dialog element to render */
    dialog: React.JSX.Element;
}

/**
 * Hook that manages AssessmentStartDialog state, journey fetching,
 * and the start flow. Returns callbacks to open the dialog and
 * a `dialog` element to render.
 */
export function useAssessmentStart(): AssessmentStartState {
    const navigate = useNavigate();
    const { selected: selectedSport } = useSelectedSport();
    const { startJourney, registerJourneyLinkIntent, activeJourneys } =
        useJourney();
    const [dialogOpen, setDialogOpen] = React.useState(false);
    const [journey, setJourney] = React.useState<JourneyWithRelations | null>(
        null,
    );

    const openStart = React.useCallback(async (journeyId: number) => {
        try {
            const j = await fetchApi<JourneyWithRelations>(
                `/api/journeys/${journeyId}`,
            );
            setJourney(j);
            setDialogOpen(true);
        } catch (err) {
            logFetchError(err);
        }
    }, []);

    const openStartWithJourney = React.useCallback(
        (j: JourneyWithRelations) => {
            setJourney(j);
            setDialogOpen(true);
        },
        [],
    );

    const inProgressInstance = React.useMemo<JourneyInstanceDTO | null>(() => {
        if (!journey) return null;
        return activeJourneys.find((j) => j.journey.id === journey.id) ?? null;
    }, [journey, activeJourneys]);

    const handleStart = React.useCallback(
        async (metadata: JourneyInstanceMetadata) => {
            if (!journey) return;
            const instance = await startJourney(journey.id, metadata);
            const workouts = journey.journeyAppWorkouts;
            if (workouts.length === 1) {
                const aw = workouts[0].appWorkout;
                await registerJourneyLinkIntent(instance.ulid, aw.id);
                navigate(
                    `/content/apps/workouts/plugin/play/${aw.appId}/${aw.id}`,
                    { replace: true },
                );
            } else {
                navigate(
                    inProgressUrl({
                        journeyType: "assessment",
                        journeyInstanceId: instance.id,
                        journeyInstanceUlid: instance.ulid,
                    }),
                    { replace: true },
                );
            }
        },
        [journey, startJourney, registerJourneyLinkIntent, navigate],
    );

    const dialog = (
        <AssessmentStartDialog
            open={dialogOpen}
            journey={journey}
            inProgressInstance={inProgressInstance}
            onClose={() => setDialogOpen(false)}
            onStart={handleStart}
            sport={selectedSport}
        />
    );

    return { openStart, openStartWithJourney, dialog };
}
